import { useCallback, useMemo, useState } from "react"
import { Box, Spinner, Text, VStack } from "@chakra-ui/react"
import { useDataConnections, type DataConnection } from "../../hooks/useDataConnections"
import { useAssetGroups } from "../../hooks/useAssetGroups"
import { DataConnectionCard } from "./DataConnectionCard"
import { ParentGroupCard } from "./ParentGroupCard"
import { AddConnectionDialog } from "./AddConnectionDialog"
import { AlignedTitles } from "@hydrologiq/shared/components/TitleWithChips"
import { DeleteDataConnectionDialog } from "@hydrologiq/shared/data-connections/DeleteDataConnectionDialog"

const DELETE_CONSEQUENCES = [
  "Remove the connection between this system and the data source",
  "Stop data monitoring from this connection",
  "Remove the connection configuration from this system",
]

import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { ScopeLinkChip } from "@hydrologiq/shared/components/ScopeLinkChip"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useSystemViewData, useSystemInfo, useSystemLoading } from "../../providers/helpers/useSystem"

import {
  groupConnectionsByParent,
  groupedAssetIds,
  selectUngroupedConnections,
  type DataConnection as ExtendedDataConnection,
  type AssetInfoEntry,
} from "../../utils/groupConnections"

interface DataConnectionsProps {
  systemId: string
}

export function DataConnections({ systemId }: DataConnectionsProps) {
  const [dialogOpen, setDialogOpen] = useState(false)

  const [pendingDelete, setPendingDelete] = useState<DataConnection | null>(null)
  const systemViewData = useSystemViewData()
  const systemInfo = useSystemInfo()
  const systemLoading = useSystemLoading()

  const {
    connections,
    loading,
    error,
    updateStatus,
    updating,
    removeConnection,
    removing,
    availableConnections,
    loadingAvailable,
    createConnection,
    creating,
  } = useDataConnections(systemId)

  // Fetch asset groups (includes groups without monitoring)
  const { groups: assetGroups, loading: loadingGroups } = useAssetGroups(systemId)

  // Track mapped asset IDs per connection for cross-connection filtering
  const [mappedAssetsByConnection, setMappedAssetsByConnection] = useState<Record<string, string[]>>({})

  const handleMappedAssetsChange = useCallback((connectionId: string, assetIds: string[]) => {
    setMappedAssetsByConnection((prev) => {
      const current = prev[connectionId]
      if (current && current.length === assetIds.length && current.every((id, i) => id === assetIds[i])) {
        return prev
      }
      return { ...prev, [connectionId]: assetIds }
    })
  }, [])

  // Build asset names map from system info, plus the modules under each parent node
  const assetNamesMap: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {}
    for (const [id, asset] of Object.entries(systemInfo?.assets ?? {})) map[id] = asset.name
    const allNodes = systemViewData
      ? [...systemViewData.isolatedNodes, ...systemViewData.installations.flatMap((ig) => ig.nodes)]
      : []
    for (const node of allNodes) {
      for (const child of node.childAssets ?? []) map[child.id] ??= child.name
    }
    return map
  }, [systemInfo, systemViewData])

  // Build asset info map from system view data (kind, volume, min_pressure)
  const assetInfoMap: Record<string, AssetInfoEntry> = useMemo(() => {
    if (!systemViewData) return {}
    const map: Record<string, AssetInfoEntry> = {}
    const allNodes = [
      ...systemViewData.isolatedNodes,
      ...systemViewData.installations.flatMap((ig) => ig.nodes),
    ]
    for (const node of allNodes) {
      if (node.asset) {
        map[node.asset.id] = {
          kind: node.asset.kind ?? null,
          volume: node.asset.volume ?? null,
          minPressure: node.asset.min_pressure ?? null,
        }
      }
      // Include child assets if present
      if (node.childAssets) {
        for (const child of node.childAssets) {
          // ChildAssetInfo doesn't have kind/volume/min_pressure — skip if already populated
          if (!map[child.id]) {
            map[child.id] = { kind: null, volume: null, minPressure: null }
          }
        }
      }
    }
    return map
  }, [systemViewData])


  // Group connections by parent asset
  const { parentGroups: connectionParentGroups } = useMemo(() => {
    const extendedConnections = connections as unknown as ExtendedDataConnection[]
    return groupConnectionsByParent(extendedConnections, {}, assetNamesMap, assetInfoMap)
  }, [connections, assetNamesMap, assetInfoMap])

  // Merge: add groups from useAssetGroups that don't already exist in connectionParentGroups
  // Only include groups that have at least one connection mapped to a child
  const parentGroups = useMemo(() => {
    const existingParentIds = new Set(connectionParentGroups.map((g) => g.parentAssetId))
    const additionalGroups: typeof connectionParentGroups = []

    for (const group of assetGroups) {
      if (!existingParentIds.has(group.parentAssetId)) {
        // Every parent group in the system is shown, even when no child is
        // actively monitored yet. A hybrid/parent has no connection of its own;
        // its connections live on the children and are managed on the parent
        // asset. Showing the group (with the management link) makes the parent
        // discoverable here regardless of the children's current state.
        const activeCount = group.children.filter((c) => c.monitoringStatus === "active").length
        const totalMappedCount = group.children.length

        additionalGroups.push({
          parentAssetId: group.parentAssetId,
          parentAssetName: group.parentAssetName,
          parentMiId: null,
          connections: [],
          children: group.children.map((c) => ({
            assetId: c.assetId,
            assetName: c.assetName,
            monitoringStatus: c.monitoringStatus,
          })),
          activeCount,
          totalMappedCount,
        })
      }
    }

    return [...connectionParentGroups, ...additionalGroups]
  }, [connectionParentGroups, assetGroups])

  // Monitoring Instances carried by Asset Group parents (nodes with child assets)
  const parentNodeMonitoringInstanceIds = useMemo(() => {
    const ids = new Set<string>()
    if (!systemViewData) return ids
    const allNodes = [
      ...systemViewData.isolatedNodes,
      ...systemViewData.installations.flatMap((ig) => ig.nodes),
    ]
    for (const node of allNodes) {
      const miId = node.assignment?.monitoringInstanceId
      if (miId && node.childAssets && node.childAssets.length > 0) ids.add(miId)
    }
    return ids
  }, [systemViewData])

  // Build ungrouped connections: connections not already shown inside a ParentGroupCard
  const ungroupedConnections = useMemo(
    () =>
      selectUngroupedConnections(
        connections as unknown as ExtendedDataConnection[],
        parentGroups,
        assetGroups,
        parentNodeMonitoringInstanceIds,
      ),
    [connections, assetGroups, parentGroups, parentNodeMonitoringInstanceIds]
  )

  // Modules are mapped on their own connections inside group cards
  const moduleAssetIds = useMemo(() => groupedAssetIds(parentGroups), [parentGroups])

  // Build per-connection exclusion set: assets mapped in OTHER connections
  const getExcludeAssetIds = useCallback((connectionId: string): Set<string> => {
    const excluded = new Set<string>(moduleAssetIds)
    for (const [connId, assetIds] of Object.entries(mappedAssetsByConnection)) {
      if (connId !== connectionId) {
        for (const id of assetIds) excluded.add(id)
      }
    }
    return excluded
  }, [mappedAssetsByConnection, moduleAssetIds])

  // Wait for groups and the system view too, or cards reshuffle after the spinner
  if (loading || loadingGroups || (systemLoading && !systemViewData)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="lg" />
      </Box>
    )
  }

  if (error) {
    return (
      <VStack align="stretch" gap={4} p={4}>
        <Text color="red.500">Failed to load data connections: {error}</Text>
        <Box>
          <ActionButton
            context={{ label: "Retry", description: "Reload data connections", variant: "secondary" }}
            onClick={() => window.location.reload()}
          />
        </Box>
      </VStack>
    )
  }

  // Asset-held connections are titled with a chip linking to their asset and
  // start collapsed, like an Asset Group; system-held ones keep the connector
  // name and their mapping open for editing.
  const renderConnectionCard = (connection: DataConnection, scope: "Asset" | "Module" | null) => {
    const name = connection.assetId ? assetNamesMap[connection.assetId] : undefined
    const scoped = scope && connection.assetId

    return (
      <DataConnectionCard
        key={connection.id}
        connection={connection}
        title={scoped ? name ?? connection.connectorName : undefined}
        showManagedBy={scope !== "Module"}
        chips={
          scoped && (
            <ScopeLinkChip
              label={scope}
              href={`/assets/${connection.assetId}/settings?tab=connections`}
              entityName={name ?? scope}
            />
          )
        }
        defaultExpanded={!scope}
        onToggleStatus={async (id: string, active: boolean, config?: { volume?: number | null; minPressure?: number | null }) => {
          try {
            await updateStatus({ monitoringInstanceId: id, active, ...config })
          } catch {
            toaster.error({ title: "An error has occurred" })
          }
        }}
        onRemove={async (id: string) => {
          const conn = connections.find((c) => c.id === id)
          if (conn) setPendingDelete(conn)
        }}
        isToggling={updating}
        isRemoving={removing}
        excludeAssetIds={getExcludeAssetIds(connection.id)}
        onMappedAssetsChange={handleMappedAssetsChange}
        assetInfoMap={assetInfoMap}
      />
    )
  }

  const assetNames = systemInfo ? Object.values(systemInfo.assets).map((a) => a.name) : []
  const siteName = systemViewData?.system?.siteName

  const hasNoContent = parentGroups.length === 0 && ungroupedConnections.length === 0

  return (
    <VStack align="stretch" gap={4} w="100%">
      {hasNoContent ? (
        <Box borderWidth="1px" borderRadius="md" p={4} opacity={0.7}>
          <Text color="gray.500">No data connections configured for this system.</Text>
        </Box>
      ) : (
        // Every card's chips line up after the longest name, Modules' included
        <AlignedTitles display="flex" flexDirection="column" rowGap={4}>
          {parentGroups.map((group) => (
            <ParentGroupCard
              key={group.parentAssetId}
              group={group}
              renderConnection={(connection) => renderConnectionCard(connection, "Module")}
            />
          ))}

          {ungroupedConnections.map((connection) =>
            renderConnectionCard(connection, connection.assetOwned ? "Asset" : null)
          )}
        </AlignedTitles>
      )}

      <Box>
        <ActionButton
          context={{ label: "Add new connection", description: "Create a new data connection", variant: "secondary" }}
          onClick={() => setDialogOpen(true)}
        />
      </Box>



      <DeleteDataConnectionDialog
        open={pendingDelete !== null}
        loading={removing}
        title="Confirm Deletion"
        consequences={
          <>
            <Text>Deleting this data connection will:</Text>
            <Box as="ul" pl={5} listStyleType="disc">
              {DELETE_CONSEQUENCES.map((item) => (
                <Box as="li" key={item}>{item}</Box>
              ))}
            </Box>
            <Text fontSize="sm" color="fg.muted">
              <Text as="span" fontWeight="semibold">Note:</Text> The{" "}
              <Text as="span" fontWeight="semibold">
                {pendingDelete?.connectorName ?? pendingDelete?.connectorType ?? ""}
              </Text>{" "}
              data connector will remain available to connect to other systems.
              Historical monitoring data will not be affected.
            </Text>
          </>
        }
        context={{
          connectorName: pendingDelete?.connectorName ?? pendingDelete?.connectorType ?? "",
          connectorType: pendingDelete?.connectorType,
          externalId: pendingDelete?.externalId,
          siteName,
          assetNames,
        }}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return
          const idToDelete = pendingDelete.id
          setPendingDelete(null)
          removeConnection(idToDelete).catch(() => {
            toaster.error({ title: "An error has occurred" })
          })
        }}
      />

      <AddConnectionDialog
        systemId={systemId}
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => setDialogOpen(false)}
        availableConnections={availableConnections}
        loadingAvailable={loadingAvailable}
        onCreateConnection={createConnection}
        creating={creating}
      />


    </VStack>
  )
}

export default DataConnections
